import React, { useState } from 'react';
import { Box, ButtonBase, List, ListItemButton, ListItemText, Menu, MenuItem, Stack, SwipeableDrawer, Typography, useMediaQuery, useTheme } from '@mui/material';
import { connect, useDispatch } from 'react-redux';
import { SitesResponse, SlateResponse, SportsResponse } from '../common/store/types';
import { setSite, setSlate, setSport, setType } from '../common/store/actions';
import { siteLogos, sportLogos } from '../utils/logos';
import { useGetSlates } from '../common/queries/slateQueries';
import { useHistory } from 'react-router-dom';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';
import { setLineupVerified } from '../preContest/store/actions';
import { filterSlates, getSupportedSports, SupportedSites } from '../common/helpers/SelectorHelpers';
import { setSelectedLineup } from '../LineupBuilder/store/actions';
import SettingsSelectors from '../common/SettingsSelectors';
import { MoonLoader } from 'react-spinners';
import { getIsMobileApp } from '../../utils/mobileApp';

interface OwnProps {
  readonly app?: string;
}

interface ReduxProps {
  readonly selectedSport: SportsResponse;
  readonly selectedSite: SitesResponse;
  readonly selectedType: string;
  readonly selectedSlate: SlateResponse;
}

interface DispatchProps {
  readonly SetSelectedSport: (v: SportsResponse) => void;
  readonly SetSelectedSite: (v: SitesResponse) => void;
  readonly SetSelectedType: (v: string) => void;
  readonly SetSelectedSlate: (v: SlateResponse) => void;
  readonly SetLineupVerified: (v: boolean) => void;
}

type Props = OwnProps & ReduxProps & DispatchProps;
type DrawerType = 'sport' | 'site' | 'slate' | 'type' | null;

const DRAWER_TITLES: Record<string, string> = {
  sport: 'League',
  site: 'Site',
  slate: 'Slate',
  type: 'Type',
};

const TYPE_OPTIONS = [
  { label: 'Classic', value: 'CLASSIC' },
  { label: 'Showdown', value: 'SHOWDOWN' },
];

const SelectorChip: React.FC<{ label: string; onClick: React.MouseEventHandler<HTMLElement> }> = ({ label, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      border: '1px solid rgba(255,255,255,0.3)',
      borderRadius: '20px',
      padding: '8px 12px',
      fontSize: '12px',
      color: 'rgba(255,255,255,0.87)',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      userSelect: 'none',
      flexShrink: 0,
    }}
  >
    {label}
  </ButtonBase>
);

const DataHubSettingsSelectors = (props: Props) => {
  const {
    selectedSite,
    selectedSlate,
    selectedType,
    selectedSport,
    app,
    SetSelectedSport,
    SetSelectedSite,
    SetSelectedSlate,
    SetSelectedType,
    SetLineupVerified,
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobileApp = getIsMobileApp();

  const [search, setSearch] = useQueryParams({ site: StringParam, slate: NumberParam });
  const history = useHistory();
  const { data, isLoading: slatesLoading } = useGetSlates(app || 'CONTESTSIM', false);
  const slates = data?.slates;
  const sports = getSupportedSports(app || 'CONTESTSIM');
  const sites = SupportedSites;
  const dispatch = useDispatch();

  const [openDrawer, setOpenDrawer] = useState<DrawerType>(null);
  const [sportAnchor, setSportAnchor] = useState<HTMLElement | null>(null);
  const [siteAnchor, setSiteAnchor] = useState<HTMLElement | null>(null);
  const [slateAnchor, setSlateAnchor] = useState<HTMLElement | null>(null);
  const [typeAnchor, setTypeAnchor] = useState<HTMLElement | null>(null);

  const filteredSlates = filterSlates(slates || [], selectedSport, selectedSite, selectedType);

  const closeAll = () => {
    setOpenDrawer(null);
    setSportAnchor(null);
    setSiteAnchor(null);
    setSlateAnchor(null);
    setTypeAnchor(null);
  };

  const handleSportSelect = (sport: SportsResponse) => {
    SetSelectedSport(sport);
    SetLineupVerified(false);
    dispatch(setSelectedLineup(null));
    const newSlate = filterSlates(slates || [], sport, selectedSite, selectedType)[0];
    if (newSlate) {
      SetSelectedSlate(newSlate);
      setSearch({ ...search, slate: parseInt(newSlate.slateId) }, 'push');
    } else {
      SetSelectedSlate({} as SlateResponse);
      setSearch({ ...search, slate: undefined }, 'push');
    }
    history.push(`${sport.sport}`);
    closeAll();
  };

  const handleSiteSelect = (site: SitesResponse) => {
    SetSelectedSite(site);
    dispatch(setSelectedLineup(null));
    const newSlate = filterSlates(slates || [], selectedSport, site, selectedType)[0];
    if (newSlate) {
      SetSelectedSlate(newSlate);
      setSearch({ ...search, slate: parseInt(newSlate.slateId), site: site.site }, 'push');
    } else {
      SetSelectedSlate({} as SlateResponse);
      setSearch({ ...search, slate: undefined, site: site.site }, 'push');
    }
    SetLineupVerified(false);
    closeAll();
  };

  const handleSlateSelect = (slate: SlateResponse) => {
    SetSelectedSlate(slate);
    dispatch(setSelectedLineup(null));
    setSearch({ ...search, slate: parseInt(slate.slateId) }, 'push');
    closeAll();
  };

  const handleTypeSelect = (type: string) => {
    SetSelectedType(type);
    dispatch(setSelectedLineup(null));
    const newSlate = filterSlates(slates || [], selectedSport, selectedSite, type)[0];
    if (newSlate) {
      SetSelectedSlate(newSlate);
      setSearch({ ...search, slate: parseInt(newSlate.slateId) }, 'push');
    } else {
      SetSelectedSlate({} as SlateResponse);
      setSearch({ ...search, slate: undefined }, 'push');
    }
    SetLineupVerified(false);
    closeAll();
  };

  if (!isMobile) {
    return <SettingsSelectors app={app} />;
  }

  if (slatesLoading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <MoonLoader color="white" size={20} />
      </Box>
    );
  }

  const slateLabel = selectedSlate?.name
    ? selectedSlate.name.length > 14
      ? selectedSlate.name.substring(0, 14) + '…'
      : selectedSlate.name
    : 'Slate';
  const siteLabel = siteLogos[selectedSite?.site?.toLowerCase()]?.name || selectedSite?.site || 'Site';
  const sportLabel = selectedSport?.sport || 'League';
  const typeLabel = selectedType
    ? selectedType.charAt(0) + selectedType.slice(1).toLowerCase()
    : 'Classic';

  const openSelector = (type: DrawerType, e: React.MouseEvent<HTMLElement>) => {
    if (isMobileApp) {
      setOpenDrawer(type);
    } else {
      if (type === 'sport') setSportAnchor(e?.currentTarget || null);
      if (type === 'site') setSiteAnchor(e?.currentTarget || null);
      if (type === 'slate') setSlateAnchor(e?.currentTarget || null);
      if (type === 'type') setTypeAnchor(e?.currentTarget || null);
    }
  };

  return (
    <Box sx={{ py: 1, px: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{ overflowX: 'auto', '&::-webkit-scrollbar': { display: 'none' } }}
      >
        <SelectorChip label={sportLabel} onClick={(e) => openSelector('sport', e)} />
        <Box sx={{ width: '1px', height: '16px', backgroundColor: 'rgba(255,255,255,0.1)', flexShrink: 0 }} />
        <SelectorChip label={siteLabel} onClick={(e) => openSelector('site', e)} />
        <SelectorChip label={typeLabel} onClick={(e) => openSelector('type', e)} />
        {filteredSlates.length > 0 && (
          <SelectorChip label={slateLabel} onClick={(e) => openSelector('slate', e)} />
        )}
      </Stack>

      {/* ── Mobile web: anchor menus ── */}
      {!isMobileApp && (
        <>
          <Menu anchorEl={sportAnchor} open={Boolean(sportAnchor)} onClose={closeAll}
            PaperProps={{ sx: { backgroundColor: '#0A0A0A', color: 'white' } }}>
            {sports?.map((sport) => {
              const logo = sportLogos[sport.sport?.toUpperCase()]?.logo;
              const invertBg = sportLogos[sport.sport?.toUpperCase()]?.invertBackground;
              return (
                <MenuItem key={sport.sport} onClick={() => handleSportSelect(sport)} sx={{ fontSize: 12, gap: 1 }}>
                  {logo && (
                    <Box sx={{ width: 20, height: 20, flexShrink: 0, backgroundColor: invertBg ? 'white' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={logo} alt={sport.sport} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </Box>
                  )}
                  {sport.sport}
                </MenuItem>
              );
            })}
          </Menu>

          <Menu anchorEl={siteAnchor} open={Boolean(siteAnchor)} onClose={closeAll}
            PaperProps={{ sx: { backgroundColor: '#0A0A0A', color: 'white' } }}>
            {sites?.map((site) => (
              <MenuItem key={site.site} onClick={() => handleSiteSelect(site)} sx={{ fontSize: 12 }}>
                {siteLogos[site.site?.toLowerCase()]?.name || site.site}
              </MenuItem>
            ))}
          </Menu>

          <Menu anchorEl={typeAnchor} open={Boolean(typeAnchor)} onClose={closeAll}
            PaperProps={{ sx: { backgroundColor: '#0A0A0A', color: 'white' } }}>
            {TYPE_OPTIONS.map(({ label, value }) => (
              <MenuItem key={value} onClick={() => handleTypeSelect(value)} sx={{ fontSize: 12 }}>
                {label}
              </MenuItem>
            ))}
          </Menu>

          <Menu anchorEl={slateAnchor} open={Boolean(slateAnchor)} onClose={closeAll}
            PaperProps={{ sx: { backgroundColor: '#0A0A0A', color: 'white' } }}>
            {filteredSlates.map((slate) => (
              <MenuItem key={slate.slateId} onClick={() => handleSlateSelect(slate)} sx={{ fontSize: 12 }}>
                {`${slate.name} (${new Date(slate.startTime).toLocaleDateString([], { month: 'short', day: '2-digit', timeZone: 'America/New_York' })} · ${new Date(slate.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' })} ET)`}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {/* ── Mobile app: bottom sheet ── */}
      {isMobileApp && (
        <SwipeableDrawer
          anchor="bottom"
          open={openDrawer !== null}
          onOpen={() => {}}
          onClose={closeAll}
          disableSwipeToOpen
          sx={{ zIndex: 1400 }}
          PaperProps={{
            sx: {
              backgroundColor: '#1A1A2E',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '60vh',
              pt: 1,
              pb: 2,
            },
          }}
        >
          <Box sx={{ width: '40px', height: '4px', backgroundColor: 'rgba(255, 255, 255, 0.3)', borderRadius: '8px', margin: '8px auto 16px' }} />

          {openDrawer && (
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1, px: 2 }}>
              {DRAWER_TITLES[openDrawer]}
            </Typography>
          )}

          <List disablePadding sx={{ overflowY: 'auto' }}>
            {openDrawer === 'sport' && sports?.map((sport) => {
              const logo = sportLogos[sport.sport?.toUpperCase()]?.logo;
              const invertBg = sportLogos[sport.sport?.toUpperCase()]?.invertBackground;
              return (
                <ListItemButton key={sport.sport} onClick={() => handleSportSelect(sport)}
                  selected={selectedSport?.sport === sport.sport}
                  sx={{ gap: 1.5, px: 2, '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.08)' } }}>
                  {logo && (
                    <Box sx={{ width: 22, height: 22, flexShrink: 0, backgroundColor: invertBg ? 'white' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={logo} alt={sport.sport} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </Box>
                  )}
                  <ListItemText primary={sport.sport} primaryTypographyProps={{ sx: { color: 'white', fontSize: 14 } }} />
                </ListItemButton>
              );
            })}

            {openDrawer === 'site' && sites?.map((site) => {
              const logo = siteLogos[site.site?.toLowerCase()]?.logo;
              return (
                <ListItemButton key={site.site} onClick={() => handleSiteSelect(site)}
                  selected={selectedSite?.site === site.site}
                  sx={{ gap: 1.5, px: 2, '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.08)' } }}>
                  {logo && (
                    <Box sx={{ width: 22, height: 22, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={logo} alt={site.site} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </Box>
                  )}
                  <ListItemText primary={siteLogos[site.site?.toLowerCase()]?.name || site.site} primaryTypographyProps={{ sx: { color: 'white', fontSize: 14 } }} />
                </ListItemButton>
              );
            })}

            {openDrawer === 'type' && TYPE_OPTIONS.map(({ label, value }) => (
              <ListItemButton key={value} onClick={() => handleTypeSelect(value)}
                selected={selectedType === value}
                sx={{ px: 2, '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.08)' } }}>
                <ListItemText primary={label} primaryTypographyProps={{ sx: { color: 'white', fontSize: 14 } }} />
              </ListItemButton>
            ))}

            {openDrawer === 'slate' && filteredSlates.map((slate) => (
              <ListItemButton key={slate.slateId} onClick={() => handleSlateSelect(slate)}
                selected={selectedSlate?.slateId === slate.slateId}
                sx={{ px: 2, '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.08)' } }}>
                <ListItemText
                  primary={slate.name}
                  secondary={`${new Date(slate.startTime).toLocaleDateString([], { month: 'short', day: '2-digit', timeZone: 'America/New_York' })} · ${new Date(slate.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' })} ET`}
                  primaryTypographyProps={{ sx: { color: 'white', fontSize: 14 } }}
                  secondaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.5)', fontSize: 11 } }}
                />
              </ListItemButton>
            ))}
          </List>
        </SwipeableDrawer>
      )}
    </Box>
  );
};

const mapStateToProps = (state: any) => ({
  selectedSlate: state.common.selectedSlate,
  selectedSport: state.common.selectedSport,
  selectedSite: state.common.selectedSite,
  selectedType: state.common.selectedType,
});

const mapDispatchToProps = {
  SetSelectedSport: setSport,
  SetSelectedSite: setSite,
  SetSelectedSlate: setSlate,
  SetSelectedType: setType,
  SetLineupVerified: setLineupVerified,
};

export default connect(mapStateToProps, mapDispatchToProps)(DataHubSettingsSelectors);
